import { useState, useEffect, useCallback, useMemo } from "react";
import Select, {
  MultiValue,
  StylesConfig,
  components,
  InputActionMeta,
  MultiValueProps,
  MultiValueRemoveProps,
} from "react-select";
import { Option } from "../../interface/Option";
import { VipAxiosInstance } from "Network/VipAxiosInstance";

interface VipOption extends Option {
  vipName?: string;
  dbId?: number;
  is_active?: boolean | null;
  status?: string;
  Status?: string;
  __inactive?: boolean;
}

interface VipProps {
  onChange: (selected: any) => void;
  clear?: boolean;
  defaultValue?: any;
  qaIdPrefix?: string;
  [key: string]: any;
}

const DEFAULT_QA_PREFIX = "vip-selector";
const qaSafe = (value: string | number | undefined | null) =>
  String(value ?? "unknown").trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const buildQaId = (prefix: string, suffix: string | number) =>
  `${qaSafe(prefix)}-${qaSafe(suffix)}`;

const qaAttrs = (prefix: string, suffix: string | number) => {
  const id = buildQaId(prefix, suffix);
  return { id, "data-testid": id, "data-qa": id };
};

const flag = (value: any): boolean | null => {
  if (value === true || value === 1 || value === "1" ||
      String(value).trim().toLowerCase() === "true") return true;
  if (value === false || value === 0 || value === "0" ||
      String(value).trim().toLowerCase() === "false") return false;
  return null;
};

// null means "status unknown", NOT "inactive".
const activeStatus = (item: any): boolean | null => {
  if (!item || typeof item !== "object") return null;
  const deleted = [
    item.is_deleted, item.isDeleted, item.is_soft_deleted,
    item.isSoftDeleted, item.is_hard_deleted, item.isHardDeleted,
  ].some(v => flag(v) === true);
  if (deleted) return false;

  const direct = flag(item.is_active ?? item.isActive ?? item.active);
  const status = String(item.Status ?? item.status ?? "").trim().toLowerCase();
  if (direct === false || ["inactive", "disabled"].includes(status)) return false;
  if (direct === true || ["active", "enabled"].includes(status)) return true;
  return null;
};

const itemId = (item: any) =>
  String(item?.dbId ?? item?.db_id ?? item?.value ?? item?.id ??
    item?.vip_id ?? item?.vipId ?? "").trim();

const isInactive = (item: VipOption) =>
  item.__inactive === true || activeStatus(item) === false;

const formatVip = (item: any): VipOption | null => {
  const id = String(item?.id ?? item?.vip_id ?? item?.vipId ?? "");
  const name = String(item?.Name ?? item?.name ?? item?.vip_name ??
    item?.Vip_name ?? item?.fullname ?? item?.full_name ?? "").trim();
  if (!id || !name) return null;
  const active = activeStatus(item);
  return {
    value: id,
    label: `(${id}) ${name}`,
    vipName: name,
    dbId: Number(id),
    is_active: active,
    status: item?.status ?? item?.Status,
    __inactive: active === false,
  } as VipOption;
};

const mergeSelected = (
  incoming: any[],
  catalogue: VipOption[],
  prior: VipOption[] = []
): VipOption[] => {
  const byId = new Map(catalogue.map(o => [itemId(o), o]));
  const previous = new Map(prior.map(o => [itemId(o), o]));
  const seen = new Set<string>();
  return incoming.map((entry: any) => {
    const id = itemId(entry);
    const matched = byId.get(id);
    const old = previous.get(id);
    // Prefer the latest backend status; if not available, retain the status
    // on the relation's selected item or from previous UI state.
    const status = matched ? activeStatus(matched) :
      (activeStatus(entry) ?? (old ? activeStatus(old) : null));
    return {
      ...(old || {}), ...entry,
      ...(matched || {}),
      value: id || String(entry?.value ?? ""),
      label: String(entry?.label ?? matched?.label ?? old?.label ?? id),
      __inactive: status === false,
      is_active: status,
    } as VipOption;
  }).filter(o => {
    const id = itemId(o);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const Vip = ({
  onChange, clear, defaultValue, qaIdPrefix = DEFAULT_QA_PREFIX,
}: VipProps) => {
  const [allOptions, setAllOptions] = useState<VipOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<VipOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const selectQaId = buildQaId(qaIdPrefix, "select");
  const inputQaId = buildQaId(qaIdPrefix, "input");

  // Fetch the complete list to know whether an already-linked VIP is inactive.
  // We filter the Add/Search menu locally, not the relation's selected items.
  const loadAllOptions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await VipAxiosInstance.get<any>("getVip", {
        params: { page: 1, limit: 1000, is_deleted: "false" },
      });
      const raw = response.data?.result ?? response.data?.vips ??
        response.data?.data ?? response.data ?? [];
      const data: any[] = Array.isArray(raw) ? raw :
        Array.isArray(raw?.data) ? raw.data : [];
      setAllOptions(data.map(formatVip).filter(
        (o: VipOption | null): o is VipOption => o !== null
      ));
    } catch (error) {
      console.error("Error loading VIP options:", error);
      setAllOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadAllOptions(); }, [loadAllOptions]);

  // Keep linked selections even when the backend returns an inactive status.
  // Never call onChange here: editing other fields must not delete VIP IDs.
  useEffect(() => {
    if (clear) return;
    if (Array.isArray(defaultValue)) {
      setSelectedOptions(previous =>
        mergeSelected(defaultValue, allOptions, previous));
    }
  }, [defaultValue, allOptions, clear]);

  useEffect(() => {
    if (clear) setSelectedOptions([]);
  }, [clear]);

  const activeMenu = useMemo(
    () => allOptions.filter(option => activeStatus(option) === true),
    [allOptions]
  );
  const options = useMemo(() => {
    const query = inputValue.trim().toLowerCase();
    return query ? activeMenu.filter(option =>
      String(option.vipName ?? "").toLowerCase().includes(query)) : activeMenu;
  }, [activeMenu, inputValue]);

  const handleChange = (next: MultiValue<VipOption>) => {
    const proposed = [...next];
    const preserved = selectedOptions.filter(
      selected => isInactive(selected) &&
      !proposed.some(option => itemId(option) === itemId(selected))
    );
    const updated = mergeSelected([...proposed, ...preserved],
      allOptions, selectedOptions);
    setSelectedOptions(updated);
    onChange(updated);
  };

  const handleInputChange = (next: string, meta: InputActionMeta): string => {
    if (meta.action === "input-change") setInputValue(next);
    return next;
  };

  const styles: StylesConfig<VipOption, true> = {
    multiValue: (base, props) => ({
      ...base,
      backgroundColor: isInactive(props.data) ? "#d1d5db" : "#007BFF",
      border: isInactive(props.data) ? "1px solid #94a3b8" : "none",
      borderRadius: "5px",
    }),
    multiValueLabel: (base, props) => ({
      ...base,
      color: isInactive(props.data) ? "#334155" : "#FFFFFF",
      fontWeight: isInactive(props.data) ? 600 : 400,
    }),
    multiValueRemove: (base, props) => ({
      ...base,
      display: isInactive(props.data) ? "none" : "flex",
      color: "#FFFFFF",
      ":hover": { backgroundColor: "#0056b3", color: "#FFFFFF" },
    }),
    option: (base, { isSelected, isFocused }) => ({
      ...base,
      backgroundColor: isSelected ? "#007BFF" :
        isFocused ? "#E6F0FA" : "#FFFFFF",
      color: isSelected ? "#FFFFFF" : "#000000",
      cursor: "pointer",
    }),
    menu: base => ({ ...base, zIndex: 999999 }),
  };

  const MultiValueComponent = (props: MultiValueProps<VipOption, true>) => (
    <div
      {...qaAttrs(qaIdPrefix, `selected-${qaSafe(itemId(props.data))}`)}
      data-selected-label={props.data.label}
      data-selected-value={props.data.value}
      data-vip-db-id={props.data.dbId ?? props.data.value}
      data-vip-name={props.data.vipName ?? ""}
      data-inactive={isInactive(props.data) ? "true" : "false"}
    >
      <components.MultiValue {...props} />
    </div>
  );

  const MultiValueRemove = (props: MultiValueRemoveProps<VipOption, true>) =>
    isInactive(props.data) ? null : <components.MultiValueRemove {...props} />;

  return (
    <div {...qaAttrs(qaIdPrefix, "wrapper")}>
      <Select<VipOption, true>
        menuPosition="absolute"
        menuPlacement="auto"
        menuShouldScrollIntoView={false}
        captureMenuScroll
        isMulti
        isClearable={false}
        closeMenuOnSelect={false}
        blurInputOnSelect={false}
        inputId={inputQaId}
        instanceId={selectQaId}
        name={selectQaId}
        classNamePrefix={selectQaId}
        aria-label="VIP selector"
        data-testid={selectQaId}
        data-qa={selectQaId}
        value={selectedOptions}
        onChange={handleChange}
        options={options}
        isLoading={loading}
        onInputChange={handleInputChange}
        inputValue={inputValue}
        placeholder="Type to search or select from list..."
        noOptionsMessage={() => loading ? "Loading..." : "No active VIP lists found"}
        styles={styles}
        components={{ MultiValue: MultiValueComponent,
          MultiValueRemove }}
      />
      <span {...qaAttrs(qaIdPrefix, "loading-state")} data-loading={loading ? "true" : "false"}
        style={{ display: "none" }}>{loading ? "Loading" : "Idle"}</span>
      <span {...qaAttrs(qaIdPrefix, "selected-count")} data-count={selectedOptions.length}
        style={{ display: "none" }}>{selectedOptions.length}</span>
      <span {...qaAttrs(qaIdPrefix, "options-count")} data-count={options.length}
        style={{ display: "none" }}>{options.length}</span>
      <span {...qaAttrs(qaIdPrefix, "input-value")} data-value={inputValue}
        style={{ display: "none" }}>{inputValue}</span>
    </div>
  );
};

export default Vip;
